import type ItinerarySearchForm from '@search/ItinerarySearchForm';
import {
    type DateSpanObject,
    type PaxCount,
    type SearchMode,
} from '@utils/types';
import { SearchFromErrorMessage } from '@search/types';
import { type ReserveType } from '@utils/itinerary';

const useSearchForm = <T extends ItinerarySearchForm = ItinerarySearchForm>(
    _isChangeSearch: boolean,
    staticReserveType?: ReserveType
) => {
    let currentSearchForm = useCurrentSearchForm();
    const analyticsStore = useAnalyticsStore();
    const searchFormStore = useSearchFormStore();
    if (staticReserveType) {
        currentSearchForm = computed(() =>
            searchFormStore.getFormModule(staticReserveType)
        );
    }
    const router = useRouter();
    const paxErrorMessage = ref<string>();
    const {
        searchMode,
        prices,
        loadingPrice,
        searchPaxModel,
        isLoadingLocations,
        capacityInfo,
    } = moduleToRefs(currentSearchForm.value);
    const destination = computed(() => currentSearchForm.value.destination);
    const onUpdateDestination = (location: T['destination']) => {
        currentSearchForm.value.setDestination(location);
        analyticsStore.searchInputSearchCount.citySelect++;
        currentSearchForm.value.loadPrices();
    };
    const destinations = computed(
        () => currentSearchForm.value.filteredDestinations
    );
    const date = computed(() => currentSearchForm.value.itinerarySearchDates);
    const onUpdateDate = (value: DateSpanObject | null) => {
        currentSearchForm.value.setItinerarySearchDates(value);
        analyticsStore.searchInputSearchCount.datePicker++;
    };

    const onSearch = (token: string) => {
        currentSearchForm.value.searchLocations(token.trim());
    };

    const onUpdatePaxModel = (
        item: PaxCount,
        value: number,
        index?: number
    ) => {
        try {
            currentSearchForm.value.updatePaxCountAndValidate({
                type: item.code,
                count: value,
                roomIndex: index,
            });
            paxErrorMessage.value = undefined;
        } catch (e: any) {
            paxErrorMessage.value = e.message;
        }
        analyticsStore.searchInputSearchCount.paxPicker++;
    };

    const onPageChange = (until: Date) =>
        currentSearchForm.value.loadPrices({ until });

    const onSubmit = async () => {
        try {
            await currentSearchForm.value.verifySearchForm(useRoute().fullPath);
            const url = await currentSearchForm.value.getSearchUrl();
            analyticsStore.resetSearchInputSearchCount();
            searchFormStore.needClearPackageSets = true;
            searchFormStore.addSearchHistory();
            // if you need a more strike direction , you can add a validator fot same date
            router.push(`${url}#${Date.now()}`);
        } catch (error) {
            if (
                (error as Error).message ===
                SearchFromErrorMessage.DuplicatePath
            ) {
                return false;
            }
            swal({
                title: 'توجه!',
                text: error as string,
                icon: 'warning',
                confirmButtonText: 'باشه',
            });
        }
        return true;
    };

    onMounted(() => {
        if (!date.value?.start) {
            onUpdateDate({ start: new Date(), end: null });
        }
    });

    return {
        onPageChange,
        destination,
        'onUpdate:destination': onUpdateDestination,
        searchMode,
        'onUpdate:searchMode': (value: SearchMode) => {
            searchMode.value = value;
        },
        destinations,
        date,
        'onUpdate:date': onUpdateDate,
        prices,
        searchPaxModel,
        'onUpdate:paxModel': onUpdatePaxModel,
        onSearch,
        onSubmit,
        loadingPrice,
        isLoadingLocations,
        paxErrorMessage,
        capacityInfo,
        'onUpdate:isLoadingLocations': (value: boolean) => {
            isLoadingLocations.value = value;
        },
    };
};

export default useSearchForm;
